import React, { useMemo } from "react";
import classnames from "classnames";

import { AutoComplete } from "fe-shared/src/components";
import type { AutoCompleteOption } from "fe-shared/src/components";

import { ParticipantOption } from "../../types";

import styles from "./ParticipantSelect.module.scss";

interface Props {
  id: string;
  options: ParticipantOption[];
  value: number | null;
  onSearchTextChange: (text: string) => void;
  onChange: (option: ParticipantOption | null) => void;
  onBlur?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  title?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  isError?: boolean;
  errorText?: string;
  className?: string;
}

const getOptionAddress = (option: ParticipantOption) =>
  [option.addressLine1, option.city].filter(Boolean).join(", ");

const toAutoCompleteOption = (
  option: ParticipantOption
): AutoCompleteOption => ({
  value: option.companyId,
  label: option.regNumber
    ? `${option.displayName} (${option.regNumber})`
    : option.displayName,
  description: getOptionAddress(option) || undefined,
});

const ParticipantSelect = (props: Props) => {
  const {
    id,
    options,
    value,
    onSearchTextChange,
    onChange,
    onBlur,
    isLoading,
    disabled,
    disabledTooltip,
    title,
    placeholder,
    required,
    isError,
    errorText,
    className,
  } = props;

  const optionsByCompanyId = useMemo(
    () => new Map(options.map((option) => [option.companyId, option])),
    [options]
  );

  const autoCompleteOptions = useMemo(
    () => options.map(toAutoCompleteOption),
    [options]
  );

  const selected = value === null ? undefined : optionsByCompanyId.get(value);

  // The list is already narrowed server-side by the typed text; this second pass matches the
  // registration numbers too, which AutoComplete's own label-only filter would drop.
  const filterOption = (option: AutoCompleteOption, query: string) => {
    const participant = optionsByCompanyId.get(Number(option.value));

    if (!participant) {
      return false;
    }

    return (
      participant.displayName.toLowerCase().includes(query) ||
      String(participant.regNumber ?? "").includes(query) ||
      (participant.registrationNumber ?? "").toLowerCase().includes(query)
    );
  };

  return (
    <div
      className={classnames(styles.wrapper, className)}
      title={disabled ? disabledTooltip : undefined}
    >
      <AutoComplete
        id={id}
        options={autoCompleteOptions}
        value={selected && toAutoCompleteOption(selected)}
        title={title}
        placeholder={placeholder ?? ""}
        required={required}
        isError={isError}
        errorText={errorText}
        disabled={disabled}
        isLoading={isLoading}
        isEmptyStateVisible
        filterOption={filterOption}
        onBlur={onBlur}
        onTextChange={(_event, text) => onSearchTextChange(text)}
        onChange={(_event, newValue) =>
          onChange(
            newValue === null
              ? null
              : optionsByCompanyId.get(Number(newValue)) ?? null
          )
        }
      />
    </div>
  );
};

export default ParticipantSelect;
